import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { useAuth } from "@/context/AuthContext";
import { Activity, Cpu, Database, RefreshCw, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUptime = (s) => {
  if (s == null) return "n/a";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m` : `${m}m ${s % 60}s`;
};

const Stat = ({ label, value, sub, tone = "text-white", testId }) => (
  <div className="whip-surface rounded-xl border border-white/10 p-5" data-testid={testId}>
    <p className="text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-white/50 mb-2">{label}</p>
    <p className={`text-3xl font-black font-mono tabular-nums ${tone}`}>{value}</p>
    {sub && <p className="text-xs text-white/45 mt-1">{sub}</p>}
  </div>
);

const WorkerRow = ({ name, state }) => {
  if (!state) return null;
  const last = state.lastRunAt ? new Date(state.lastRunAt).toLocaleString() : "not yet";
  const errs = Array.isArray(state.errors) ? state.errors.length : state.lastRunErrors ?? 0;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 py-2.5 border-b border-white/5 last:border-0" data-testid={`worker-${name}`}>
      <span className="text-sm text-white/85 font-medium">{name}</span>
      <div className="flex items-center gap-4 text-xs font-mono text-white/50">
        {state.running && <span className="text-orange-300">running</span>}
        <span>last: {last}</span>
        {state.lastDurationSec != null && <span>{state.lastDurationSec}s</span>}
        <span className={errs ? "text-rose-300" : "text-emerald-300"}>{errs} err</span>
      </div>
    </div>
  );
};

export default function AdminSystem() {
  const { user } = useAuth();
  const [sys, setSys] = useState(null);
  const [trimming, setTrimming] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/system`);
      setSys(r.data);
    } catch {
      toast.error("Failed to load system status");
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const trim = async () => {
    setTrimming(true);
    try {
      const r = await axios.post(`${API}/admin/memory/trim`);
      toast.success(`Freed ${r.data.freedMb} MB, now ${r.data.rssMb} MB`);
      load();
    } catch {
      toast.error("Trim failed");
    } finally {
      setTrimming(false);
    }
  };

  if (user && user.role !== "admin") {
    return (
      <div className="min-h-screen" style={{ background: "var(--whip-bg, #0b0b0f)" }}>
        <Navigation />
        <p className="text-center text-white/55 py-24">Admin access required.</p>
      </div>
    );
  }

  const mem = sys?.memory;
  const pct = mem?.usedPct;
  const tone = pct == null ? "text-white" : pct > 78 ? "text-rose-300" : pct > 60 ? "text-amber-300" : "text-emerald-300";

  return (
    <div className="min-h-screen" style={{ background: "var(--whip-bg, #0b0b0f)" }}>
      <Navigation />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-system-page">
        <div className="flex items-center justify-between gap-4 mb-8">
          <h1 className="text-2xl font-black text-white flex items-center gap-2.5">
            <Cpu className="w-6 h-6 text-orange-400" /> System Health
          </h1>
          <div className="flex items-center gap-2">
            <button onClick={load} className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white/80 hover:bg-white/5" data-testid="system-refresh-btn">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button onClick={trim} disabled={trimming} className="inline-flex items-center gap-1.5 rounded-lg bg-orange-500/15 border border-orange-500/40 px-3 py-1.5 text-xs text-orange-200 hover:bg-orange-500/25 disabled:opacity-50" data-testid="system-trim-btn">
              <Trash2 className="w-3.5 h-3.5" /> {trimming ? "Trimming…" : "Trim memory"}
            </button>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <Stat label="Memory (RSS)" value={mem ? `${Math.round(mem.rssMb)} MB` : "n/a"} sub={mem?.limitMb ? `of ${Math.round(mem.limitMb)} MB pod limit` : "no cgroup limit detected"} tone={tone} testId="stat-memory" />
          <Stat label="Memory used" value={pct != null ? `${pct}%` : "n/a"} sub={mem?.underPressure ? "UNDER PRESSURE, prewarmers paused" : `pressure gate at ${Math.round((mem?.pressureRatio || 0.78) * 100)}%`} tone={tone} testId="stat-memory-pct" />
          <Stat label="Uptime" value={fmtUptime(sys?.uptimeSeconds)} sub={sys?.startedAt ? `since ${new Date(sys.startedAt).toLocaleString()}` : ""} testId="stat-uptime" />
          <Stat label="Refreshed" value={sys?.time ? new Date(sys.time).toLocaleTimeString() : "n/a"} sub="auto-refreshes every 15s" testId="stat-refreshed" />
        </div>

        {mem?.limitMb && (
          <div className="h-2 rounded-full bg-white/10 overflow-hidden mb-8" data-testid="memory-bar">
            <div className={`h-full transition-[width] duration-700 ${pct > 78 ? "bg-rose-400" : pct > 60 ? "bg-amber-400" : "bg-emerald-400"}`} style={{ width: `${Math.min(100, pct || 0)}%` }} />
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-2">
          <section className="whip-surface rounded-xl border border-white/10 p-5" data-testid="workers-panel">
            <p className="text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-white/60 mb-3 flex items-center gap-2">
              <Activity className="w-3.5 h-3.5 text-orange-400" /> Background workers
            </p>
            <WorkerRow name="Party prewarm" state={sys?.workers?.partyPrewarm} />
            <WorkerRow name="Factions prewarm" state={sys?.workers?.factionsPrewarm} />
            <WorkerRow name="AI analysis prewarm" state={sys?.workers?.analysisPrewarm} />
            <WorkerRow name="Forecast prewarm" state={sys?.workers?.forecastPrewarm} />
          </section>
          <section className="whip-surface rounded-xl border border-white/10 p-5" data-testid="caches-panel">
            <p className="text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-white/60 mb-3 flex items-center gap-2">
              <Database className="w-3.5 h-3.5 text-orange-400" /> Persistent caches (Mongo)
            </p>
            {Object.entries(sys?.caches || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 text-sm" data-testid={`cache-${k}`}>
                <span className="text-white/75">{k.replace(/([A-Z])/g, " $1").toLowerCase()}</span>
                <span className="font-mono text-white/90">{v ?? "n/a"}</span>
              </div>
            ))}
          </section>
        </div>
      </main>
    </div>
  );
}
